import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { has } from 'lodash';

import { get, post, API_AUTH_URL, API_LOGOUT_URL } from 'api';
import { actions as userActions, SET_EXPIRY } from 'apps/core/user/store';
import { REGISTRATION_OPEN } from './config';

import { BrandFooter, BrandHeader, SET_SITE_ROLE } from 'apps/core/site_options/store';
import { addNotification, removeNotification } from 'apps/core/flash_notifications/actions';
import { Button } from 'bs';

import WithRouteProps from 'components/WithRouteProps';
import { add, diff } from 'utils/dates';

import _Login from './Login';
import Register from './Register';
import ConfirmRegistration from './ConfirmRegistration';
import RequestPasswordReset from './RequestPasswordReset';
import ResetPassword from './ResetPassword';

// reducers

const DEFAULT_LOGIN_ALERT = `Please sign in ${REGISTRATION_OPEN && 'or create an account'} to access your policy online.`;

export const defaultState = {
    needAuth: false,
    loginAlert: DEFAULT_LOGIN_ALERT,
    loggedIn: false,
    loggingIn: false,
    pendingMFA: false,
    pendingSiteSelection: false,
    registrationOpen: REGISTRATION_OPEN,
    mfaInstructions: {},
    siteSelectionInstructions: {},
    meta: {
        error: '',
        fieldErrors: {},
        submitted: false,
        submitting: false,
    },
};

// reducer variables
const LOGOUT = 'LOGOUT';
const LOGIN = 'LOGIN';
const LOGIN_ERROR = 'LOGIN_ERROR';
const LOGIN_REQUIRED = 'LOGIN_REQUIRED';
const LOGIN_PENDING_MFA = 'LOGIN_PENDING_MFA';
const LOGIN_PENDING_SITE_SELECTION = 'LOGIN_PENDING_SITE_SELECTION';
const LOGGED_IN = 'LOGGED_IN';
const CONFIGURE_LOGIN = 'CONFIGURE_LOGIN';

export const reducer = (state = defaultState, action = {}) => {
    switch (action.type) {
        case LOGOUT:
            return defaultState;

        case LOGIN_REQUIRED:
            return { ...defaultState, needAuth: true, loggedIn: false };

        case LOGIN:
            return { ...defaultState, needAuth: false, loggedIn: true };

        case LOGGED_IN:
            return { ...defaultState, needAuth: false, loggedIn: true };

        case LOGIN_PENDING_MFA:
            return {
                ...defaultState,
                needAuth: true,
                pendingMFA: true,
                mfaInstructions: action.mfaInstructions,
            };

        case LOGIN_PENDING_SITE_SELECTION:
            return {
                ...defaultState,
                needAuth: true,
                pendingSiteSelection: true,
                siteSelectionInstructions: action.instructions,
            };

        case LOGIN_ERROR:
            return {
                ...defaultState,
                needAuth: true,
            };

        case CONFIGURE_LOGIN:
            return {
                ...defaultState,
                loginAlert: action.loginAlert || defaultState.loginAlert,
                registrationOpen: has(action, 'registrationOpen') ? action.registrationOpen : defaultState.registrationOpen,
            };

        default:
            return state;
    }
};

// actions

export const logout = () => {
    return dispatch => {
        get(API_LOGOUT_URL)
            .then(resp => {
                dispatch({ type: 'LOGOUT' });
                window.location.href = '/';
            })
            .catch(error => {
                dispatch({ type: LOGIN_ERROR });
                window.location.href = '/';
            });
    };
};


export const configureLogin = ({
    loginAlert = DEFAULT_LOGIN_ALERT,
    registrationOpen = REGISTRATION_OPEN,
}) => {
    return dispatch => dispatch({ type: CONFIGURE_LOGIN, loginAlert, registrationOpen });
};


const TimeoutMessage = ({ expires, extendSession, expireSession }) => {
    const [clock, setClock] = useState(diff(expires, 'seconds'));
    let intervalId = useRef(null);

    useEffect(() => {
        if (clock <= 0) {
            expireSession();
        } else {
            intervalId.current = setInterval(() => setClock(diff(expires, 'seconds')), 1000);
        }
    });

    return (
        <>
            <p>
                You will be logged out in {clock} seconds.
            </p>
            <Button flavor='primary' onClick={extendSession}>Stay logged in</Button>
        </>
    );
};

export const AuthWatcher = () => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const site_options = useSelector(state => state.site_options);
    const { session_expiry } = user;
    const { brand } = site_options;
    const anonRole = `${brand.slug}_anon`;

    const [ sent, setSent ] = useState(false);

    const NOTIFICATION_TIMEOUT = 60;
    const WIGGLE_ROOM = 30;

    useEffect(() => {
        if (session_expiry && !sent) {
            const showTime = add(-NOTIFICATION_TIMEOUT, 'seconds', session_expiry);
            const millsTillShowTime = diff(showTime, 'milliseconds');

            const extendSession = () => {
                get('extend-session/')
                    .then(resp => {
                        dispatch({type: SET_EXPIRY, session_expiry: resp.expires});
                        dispatch(removeNotification('timeoutmessage'));
                        setSent(false);
                    })
                    .catch(error => {
                        dispatch({type: 'LOGIN_ERROR', error: 'Please login.' });
                    });
            };

            const expireSession = () => {
                dispatch({type: LOGIN_REQUIRED});
                dispatch({type: SET_SITE_ROLE, brand: brand, role: anonRole});
                dispatch(removeNotification('timeoutmessage'));
            };

            setTimeout(() => {
                dispatch(addNotification({
                    message: (
                        <TimeoutMessage
                            expires={session_expiry}
                            extendSession={extendSession}
                            expireSession={expireSession}
                        />
                    ),
                    timeout: (NOTIFICATION_TIMEOUT + WIGGLE_ROOM) * 1000,
                    id: 'timeoutmessage',
                    flavor: 'info',
                }));
            }, millsTillShowTime);
            setSent(true);
        }
    }, [session_expiry, dispatch, sent, setSent, brand, NOTIFICATION_TIMEOUT, WIGGLE_ROOM]);
};


function loginWithCredentials(email, password, callbacks) {
    return dispatch => {
        callbacks.start();
        post(API_AUTH_URL, { email, password })
            .then(data => {
                if (data.expires) {
                    dispatch({ type: LOGIN, ...data });
                    dispatch(userActions.fetchUser());
                    callbacks.done();
                } else if (data.device || data.email) {
                    dispatch({ type: LOGIN_PENDING_MFA, mfaInstructions: data });
                    callbacks.done();
                } else if (data.roles) {
                    dispatch({ type: LOGIN_PENDING_SITE_SELECTION, instructions: data });
                    callbacks.done();
                }
            })
            .catch(error => {
                dispatch({
                    type: LOGIN_ERROR,
                });
                callbacks.fail(error);
            });
    };
}


export function loginWithTokenUrl(url, callbacks) {
    return dispatch => {
        callbacks.start();
        post(url)
            .then(data => {
                if (data.expires) {
                    dispatch({ type: LOGIN, ...data });
                    dispatch(userActions.fetchUser());
                    callbacks.done();
                } else {
                    // compare to loginWithCredentials above
                    // We only allow non-MFA, provider role only at this endpoint,
                    // so any other response than auth success is an error
                    dispatch({
                        type: LOGIN_ERROR,
                    });
                    callbacks.fail(new Error("Invalid login"));
                }
            })
            .catch(error => {
                dispatch({
                    type: LOGIN_ERROR,
                });
                callbacks.fail(error);
            });
    };
}


function loginWithMFA(url, data, callbacks) {
    return dispatch => {
        callbacks.start();
        post(url, data)
            .then(data => {
                if (data.roles) {
                    dispatch({ type: LOGIN_PENDING_SITE_SELECTION, instructions: data });
                    callbacks.done();
                } else {
                    dispatch({ type: LOGIN, ...data });
                    dispatch(userActions.fetchUser());
                    callbacks.done();
                }
            })
            .catch(error => {
                callbacks.fail(error);
            });
    };
}


function loginWithSiteSelection(url, data, callbacks) {
    return dispatch => {
        callbacks.start();
        post(url, data)
            .then(data => {
                dispatch({ type: LOGIN, ...data });
                dispatch(userActions.fetchUser());
                callbacks.done();
            })
            .catch(error => {
                callbacks.fail(error);
            });
    };
}

export const actions = { loginWithCredentials, loginWithMFA };

// components

const Login = connect(
    ({ auth }, ownProps) => ({
        loginAlert: auth.loginAlert,
        pendingMFA: auth.pendingMFA,
        mfaInstructions: auth.mfaInstructions,
        pendingSiteSelection: auth.pendingSiteSelection,
        registrationOpen: auth.registrationOpen,
        siteSelectionInstructions: auth.siteSelectionInstructions,
    }),
    (dispatch, ownProps) => ({
        login: (email, password, callbacks) =>
            dispatch(loginWithCredentials(email, password, callbacks)),
        loginWithMFA: (url, data, callbacks) =>
            dispatch(loginWithMFA(url, data, callbacks)),
        loginWithSiteSelection: (url, data, callbacks) =>
            dispatch(loginWithSiteSelection(url, data, callbacks)),
    })
)(_Login);

export const Auth = () => {
    const dispatch = useDispatch();

    return (
        <div className='flex-d h100vh flex-dir-col'>
            <div className='flex-1-auto'>
                <BrandHeader />
                <Switch>
                    <Route path='/reset-password/:id/:token'>
                        <WithRouteProps render={({ params, navigate }) => {
                            const { id, token } = params;
                            return (
                                <>
                                    <Helmet title='Reset Password' />
                                    <ResetPassword
                                        id={id}
                                        token={token}
                                        goHome={() => navigate('/')}
                                    />
                                </>
                            );
                        }} />
                    </Route>
                    <Route path='/reset-password'>
                        <WithRouteProps render={({ navigate }) => (
                            <>
                                <Helmet title='Reset Password' />
                                <RequestPasswordReset
                                    goHome={() => navigate('/')}
                                />
                            </>
                        )} />
                    </Route>
                    {REGISTRATION_OPEN && (
                        <Route path='/register/:id/:token'>
                            <WithRouteProps render={({ params }) => {
                                const { id, token } = params;
                                return (
                                    <>
                                        <Helmet title='Confirm Registration' />
                                        <ConfirmRegistration
                                            id={id}
                                            token={token}
                                            onSuccess={() => {
                                                dispatch(configureLogin({
                                                    loginAlert: "Registration verified! Please login.",
                                                    registrationOpen: false
                                                }));
                                            }}
                                        />
                                    </>
                                );
                            }} />
                        </Route>
                    )}
                    {REGISTRATION_OPEN && (
                        <Route path='/register'>
                            <WithRouteProps render={({ navigate }) => (
                                <>
                                    <Helmet title='Register' />
                                    <Register onCancel={() => navigate('/')} />
                                </>
                            )} />
                        </Route>
                    )}
                    <Route path='/'>
                        <Login />
                    </Route>
                </Switch>
            </div>
            <BrandFooter />
        </div>
    );
};
